<template>
  <div class="dashboard-wrapper">
    <!-- 顶部教室tab切换区 -->
    <div class="dashboard-tabs">
      <div
        v-for="(room, idx) in rooms"
        :key="room"
        :class="['tab-btn', {active: idx === activeRoomIndex}]"
        @click="selectRoom(idx)"
      >
        {{ room }}
      </div>
      <div class="tab-btn more"><i class="el-icon-more"></i></div>
      <div class="dashboard-actions">
        <el-button type="primary" size="small" @click="addRoom">新增</el-button>
        <el-button size="small" @click="removeRoom">删除</el-button>
      </div>
    </div>
    <el-row :gutter="24" class="dashboard-main">
      <!-- 左卡片：设备统计 -->
      <el-col :span="12">
        <el-card class="dashboard-card">
          <div class="device-stats">
            <div class="stat-item" v-for="item in deviceStats" :key="item.label">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-value">{{ item.value }}</div>
            </div>
          </div>
          <el-tabs v-model="activeDeviceTab" class="device-tabs">
            <el-tab-pane label="设备效率管理" name="efficiency"></el-tab-pane>
            <el-tab-pane label="历史故障类" name="history"></el-tab-pane>
            <el-tab-pane label="热力地图" name="heat"></el-tab-pane>
            <el-tab-pane label="故障检修报表" name="repair"></el-tab-pane>
          </el-tabs>
          <div class="chart-area">
            <div ref="barChartRef" style="height:260px;width:100%"></div>
          </div>
        </el-card>
      </el-col>
      <!-- 右卡片：教室加工统计 -->
      <el-col :span="12">
        <el-card class="dashboard-card">
          <div class="workshop-stats">
            <div class="stat-item" v-for="item in workshopStats" :key="item.label">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-value" :class="{'highlight': item.highlight}">{{ item.value }}</div>
            </div>
          </div>
          <div class="chart-area">
            <div ref="lineChartRef" style="height:260px;width:100%"></div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import * as echarts from 'echarts';

export default defineComponent({
  name: 'Dashboard',
  setup() {
    const rooms = ref(['教室一', '教室二', '教室三', '教室四', '教室五']);
    const activeRoomIndex = ref(0);
    const activeDeviceTab = ref('efficiency');

    // 正确的DOM引用
    const barChartRef = ref(null);
    const lineChartRef = ref(null);

    // 五个教室的专属数据
    const allDeviceStats = [
      [ // 教室一
        { label: '设备在线数量', value: 35 },
        { label: '设备故障数', value: 2 },
        { label: '设备待机数', value: 3 },
        { label: '设备离线数', value: 1 },
      ],
      [ // 教室二
        { label: '设备在线数量', value: 28 },
        { label: '设备故障数', value: 5 },
        { label: '设备待机数', value: 6 },
        { label: '设备离线数', value: 2 },
      ],
      [ // 教室三
        { label: '设备在线数量', value: 40 },
        { label: '设备故障数', value: 1 },
        { label: '设备待机数', value: 2 },
        { label: '设备离线数', value: 0 },
      ],
      [ // 教室四
        { label: '设备在线数量', value: 22 },
        { label: '设备故障数', value: 3 },
        { label: '设备待机数', value: 8 },
        { label: '设备离线数', value: 4 },
      ],
      [ // 教室五
        { label: '设备在线数量', value: 31 },
        { label: '设备故障数', value: 0 },
        { label: '设备待机数', value: 5 },
        { label: '设备离线数', value: 3 },
      ]
    ];
    const allWorkshopStats = [
      [ // 教室一
        { label: '任务已完成', value: 723 },
        { label: '目标任务数', value: 1600 },
        { label: '待执行任务', value: 590 },
        { label: '任务完成率', value: '56.7%', highlight: true },
      ],
      [ // 教室二
        { label: '任务已完成', value: 1012 },
        { label: '目标任务数', value: 2000 },
        { label: '待执行任务', value: 850 },
        { label: '任务完成率', value: '59.5%', highlight: true },
      ],
      [ // 教室三
        { label: '任务已完成', value: 430 },
        { label: '目标任务数', value: 1200 },
        { label: '待执行任务', value: 300 },
        { label: '任务完成率', value: '35.8%', highlight: true },
      ],
      [ // 教室四
        { label: '任务已完成', value: 888 },
        { label: '目标任务数', value: 1700 },
        { label: '待执行任务', value: 600 },
        { label: '任务完成率', value: '52.2%', highlight: true },
      ],
      [ // 教室五
        { label: '任务已完成', value: 1500 },
        { label: '目标任务数', value: 2200 },
        { label: '待执行任务', value: 900 },
        { label: '任务完成率', value: '68.2%', highlight: true },
      ]
    ];

    // 图表option也根据教室切换
    const allBarOptions = [
      // 教室一
      () => getBarOption(activeDeviceTab.value),
      // 教室二
      () => ({ ...getBarOption(activeDeviceTab.value), series: [{ ...getBarOption(activeDeviceTab.value).series[0], data: [60, 80, 70, 90, 50, 30] }] }),
      // 教室三
      () => ({ ...getBarOption(activeDeviceTab.value), series: [{ ...getBarOption(activeDeviceTab.value).series[0], data: [40, 30, 60, 50, 90, 70] }] }),
      // 教室四
      () => ({ ...getBarOption(activeDeviceTab.value), series: [{ ...getBarOption(activeDeviceTab.value).series[0], data: [20, 10, 30, 40, 60, 50] }] }),
      // 教室五
      () => ({ ...getBarOption(activeDeviceTab.value), series: [{ ...getBarOption(activeDeviceTab.value).series[0], data: [90, 100, 80, 60, 30, 20] }] }),
    ];
    const allLineOptions = [
      // 教室一
      () => ({
        tooltip: { trigger: 'axis' },
        legend: { data: ['平均节拍时长','平均等待时长','平均停机时长'], top: 10 },
        xAxis: { type: 'category', data: ['7:00','9:00','11:00','13:00','15:00','17:00','18:00'] },
        yAxis: { type: 'value' },
        series: [
          { name: '平均节拍时长', type: 'line', data: [100, 140, 180, 230, 320, 420, 500], smooth: true, color:'#3DC47E' },
          { name: '平均等待时长', type: 'line', data: [300, 320, 350, 400, 450, 480, 520], smooth: true, color:'#2EA2FA' },
          { name: '平均停机时长', type: 'line', data: [120, 110, 130, 150, 190, 230, 260], smooth: true, color:'#FF6B6B' }
        ],
        grid: { left: 40, right: 30, top: 40, bottom: 30 },
      }),
      // 教室二
      () => ({ ...this.lineChartOption, series: [
        { name: '平均节拍时长', type: 'line', data: [200, 120, 180, 250, 220, 320, 400], smooth: true, color:'#3DC47E' },
        { name: '平均等待时长', type: 'line', data: [150, 220, 250, 300, 350, 380, 420], smooth: true, color:'#2EA2FA' },
        { name: '平均停机时长', type: 'line', data: [80, 90, 100, 120, 140, 160, 180], smooth: true, color:'#FF6B6B' }
      ] }),
      // 教室三
      () => ({ ...this.lineChartOption, series: [
        { name: '平均节拍时长', type: 'line', data: [50, 60, 70, 80, 90, 100, 110], smooth: true, color:'#3DC47E' },
        { name: '平均等待时长', type: 'line', data: [200, 180, 160, 140, 120, 100, 80], smooth: true, color:'#2EA2FA' },
        { name: '平均停机时长', type: 'line', data: [40, 50, 60, 70, 80, 90, 100], smooth: true, color:'#FF6B6B' }
      ] }),
      // 教室四
      () => ({ ...this.lineChartOption, series: [
        { name: '平均节拍时长', type: 'line', data: [300, 280, 260, 240, 220, 200, 180], smooth: true, color:'#3DC47E' },
        { name: '平均等待时长', type: 'line', data: [100, 120, 140, 160, 180, 200, 220], smooth: true, color:'#2EA2FA' },
        { name: '平均停机时长', type: 'line', data: [60, 70, 80, 90, 100, 110, 120], smooth: true, color:'#FF6B6B' }
      ] }),
      // 教室五
      () => ({ ...this.lineChartOption, series: [
        { name: '平均节拍时长', type: 'line', data: [400, 420, 440, 460, 480, 500, 520], smooth: true, color:'#3DC47E' },
        { name: '平均等待时长', type: 'line', data: [300, 320, 340, 360, 380, 400, 420], smooth: true, color:'#2EA2FA' },
        { name: '平均停机时长', type: 'line', data: [220, 240, 260, 280, 300, 320, 340], smooth: true, color:'#FF6B6B' }
      ] })
    ];

    // 当前教室的统计数据
    const deviceStats = ref([...allDeviceStats[activeRoomIndex.value]]);
    const workshopStats = ref([...allWorkshopStats[activeRoomIndex.value]]);
    const barChartOption = ref(allBarOptions[activeRoomIndex.value]());
    const lineChartOption = ref(allLineOptions[activeRoomIndex.value]());

    // 监听教室切换
    watch(activeRoomIndex, (idx) => {
      deviceStats.value = [...allDeviceStats[idx]];
      workshopStats.value = [...allWorkshopStats[idx]];
      barChartOption.value = allBarOptions[idx]();
      if (barChart) barChart.setOption(barChartOption.value, true);
      lineChartOption.value = allLineOptions[idx]();
      if (lineChart) lineChart.setOption(lineChartOption.value, true);
    });

    // 不同tab对应的option
    function getBarOption(tab) {
      if (tab === 'efficiency') {
        return {
          tooltip: {},
          xAxis: { type: 'value', max: 100 },
          yAxis: { type: 'category', data: ['设备A','设备B','设备C','设备D','设备E','设备F'] },
          series: [{ type: 'bar', data: [90, 70, 50, 40, 30, 20], itemStyle: { color: '#2EA2FA', borderRadius: [6,6,6,6] }, barWidth: 18 }],
          grid: { left: 60, right: 30, top: 30, bottom: 30 },
        };
      } else if (tab === 'history') {
        return {
          tooltip: {},
          xAxis: { type: 'value', max: 20 },
          yAxis: { type: 'category', data: ['设备A','设备B','设备C','设备D','设备E','设备F'] },
          series: [{ type: 'bar', data: [2, 5, 1, 0, 3, 2], itemStyle: { color: '#FF6B6B', borderRadius: [6,6,6,6] }, barWidth: 18 }],
          grid: { left: 60, right: 30, top: 30, bottom: 30 },
        };
      } else if (tab === 'heat') {
        return {
          tooltip: {},
          xAxis: { type: 'value', max: 100 },
          yAxis: { type: 'category', data: ['设备A','设备B','设备C','设备D','设备E','设备F'] },
          series: [{ type: 'bar', data: [10, 60, 80, 30, 50, 90], itemStyle: { color: '#FFD700', borderRadius: [6,6,6,6] }, barWidth: 18 }],
          grid: { left: 60, right: 30, top: 30, bottom: 30 },
        };
      } else if (tab === 'repair') {
        return {
          tooltip: {},
          xAxis: { type: 'value', max: 10 },
          yAxis: { type: 'category', data: ['设备A','设备B','设备C','设备D','设备E','设备F'] },
          series: [{ type: 'bar', data: [3, 1, 0, 2, 1, 2], itemStyle: { color: '#3DC47E', borderRadius: [6,6,6,6] }, barWidth: 18 }],
          grid: { left: 60, right: 30, top: 30, bottom: 30 },
        };
      }
      return {};
    }

    let barChart = null;
    let lineChart = null;

    // 监听tab切换，动态切换option
    watch(activeDeviceTab, (newTab) => {
      barChartOption.value = getBarOption(newTab);
      if (barChart) barChart.setOption(barChartOption.value, true);
    });

    onMounted(() => {
      barChart = echarts.init(barChartRef.value);
      barChart.setOption(barChartOption.value);
      lineChart = echarts.init(lineChartRef.value);
      lineChart.setOption(lineChartOption.value);
      window.addEventListener('resize', resizeCharts);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeCharts);
      barChart && barChart.dispose();
      lineChart && lineChart.dispose();
    });
    function resizeCharts() {
      barChart && barChart.resize();
      lineChart && lineChart.resize();
    }

    function selectRoom(idx) { activeRoomIndex.value = idx; }
    function addRoom() { /* 新增教室逻辑 */ }
    function removeRoom() { /* 删除教室逻辑 */ }

    return {
      rooms,
      activeRoomIndex,
      activeDeviceTab,
      deviceStats,
      workshopStats,
      barChartOption,
      lineChartOption,
      selectRoom,
      addRoom,
      removeRoom,
      barChartRef,
      lineChartRef
    };
  }
})
</script>

<style scoped>
.dashboard-wrapper {
  background: #EBF6FE;
  border-radius: 18px;
  padding: 24px;
  min-height: 100vh;
}
.dashboard-tabs {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  background: #fff;
  border-radius: 10px;
  padding: 15px 32px;
}
.tab-btn {
  background: #fff;
  border: 1.5px solid #2EA2FA;
  color: #2EA2FA;
  font-size: 20px;
  border-radius: 10px;
  padding: 4px 32px;
  margin-right: 16px;
  cursor: pointer;
  transition: all 0.2s;
}
.tab-btn.active {
  background: #2EA2FA;
  color: #fff;
  font-weight: 500;
}
.tab-btn.more {
  padding: 4px 12px;
  font-size: 22px;
  border: none;
  background: transparent;
}
.dashboard-actions {
  margin-left: auto;
  display: flex;
  gap: 12px;
}
.dashboard-main {
  margin-top: 6px;
}
.dashboard-card {
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 4px 18px 0 rgba(46,162,250,0.08);
  padding: 24px 20px 12px 20px;
  min-height: 550px;
}
.device-stats, .workshop-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}
.stat-label {
  color: #666;
  font-size: 15px;
  margin-bottom: 8px;
}
.stat-value {
  color: #2EA2FA;
  font-size: 26px;
  font-weight: 600;
}
.stat-value.highlight {
  color: #2EA2FA;
  font-size: 26px;
  font-weight: 700;
}
.device-tabs {
  margin-bottom: 10px;
}
.chart-area {
  width: 100%;
  min-height: 240px;
}
</style>
